import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../components/Logo';
import Toast from '../../components/Toast';
import { supabase } from '../../supabaseClient';
import { LAST_AUTH_EMAIL, ACCOUNT_DELETED_FLAG } from '../../constants/storageKeys';
import { normalizeGoogleHl, detectOAuthError } from '../../utils/oauth';
import { isDeletionCooldownError } from '../../utils/authError';
import { isInAppBrowser, isAndroid, isIOS } from '../../utils/browser';
import { useOpenInExternalBrowser } from '../../hooks/useOpenInExternalBrowser';
import InAppBrowserNotice from '../../components/InAppBrowserNotice';
import PwaInstallButton from '../../components/PwaInstallButton';
import PwaInstallGuide from '../../components/PwaInstallGuide';

const COOLDOWN_SECONDS = 60;

export default function Auth() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [toastConfig, setToastConfig] = useState({ message: '', duration: 2500 });

  // PWA 설치 안내 오버레이 활성 반응형 상태
  const [isGuideActive, setIsGuideActive] = useState(() => {
    try {
      return typeof window !== 'undefined' && sessionStorage.getItem('PWA_INSTALL_GUIDE') === '1';
    } catch (_) {
      return false;
    }
  });
  const [isInstallNoticeOpen, setIsInstallNoticeOpen] = useState(false);

  const handleCloseGuide = () => {
    setIsGuideActive(false);
    try {
      sessionStorage.removeItem('PWA_INSTALL_GUIDE');
    } catch (_) {}
  };

  useEffect(() => {
    if (location.state?.toast) {
      setToastConfig({ message: t(location.state.toast), duration: 3000 });
      window.history.replaceState({}, document.title);
    }
  }, [location.state, t]);

  useEffect(() => {
    let deleted = null;
    try {
      deleted = sessionStorage.getItem(ACCOUNT_DELETED_FLAG);
      if (deleted) sessionStorage.removeItem(ACCOUNT_DELETED_FLAG);
    } catch {}
    if (deleted) {
      setToastConfig({ message: t('profile.delete_account_success'), duration: 3000 });
    }
  }, [t]);

  // 구글 OAuth 취소 또는 에러 감지 및 안내
  useEffect(() => {
    const err = detectOAuthError();
    if (err) {
      if (err.isCooldown) {
        setToastConfig({ message: t('auth.error_deletion_cooldown'), duration: 4000 });
      } else if (err.isCancelled) {
        setToastConfig({ message: t('auth.google_login_cancelled'), duration: 3000 });
      } else {
        setToastConfig({ message: t('auth.google_login_error'), duration: 3000 });
      }
      try {
        window.history.replaceState({}, '', window.location.pathname);
      } catch (_) {}
    }
  }, [t]);

  const [step, setStep] = useState(() => {
    return sessionStorage.getItem('auth_step') || 'email';
  });
  const [email, setEmail] = useState(() => {
    const sessionEmail = sessionStorage.getItem('auth_email');
    if (sessionEmail) return sessionEmail;
    try {
      return localStorage.getItem(LAST_AUTH_EMAIL) || '';
    } catch (_) {
      return '';
    }
  });
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [lastSentAt, setLastSentAt] = useState(null);
  const [canShowSpamNotice, setCanShowSpamNotice] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [inappNotice, setInappNotice] = useState(null);
  const { openInBrowser, isOpening } = useOpenInExternalBrowser();

  // 자동 실행 조건 동기 판정 (초기 렌더링 시 로그인 폼 노출 0프레임 보장)
  const [isAutoOAuthRedirecting, setIsAutoOAuthRedirecting] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (isInAppBrowser()) return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get('auto_oauth') !== '1') return false;
    if (sessionStorage.getItem('AUTO_OAUTH_CONSUMED') === '1') return false;
    if (detectOAuthError()) return false;
    return true;
  });

  useEffect(() => {
    sessionStorage.setItem('auth_step', step);
  }, [step]);

  useEffect(() => {
    sessionStorage.setItem('auth_email', email);
  }, [email]);

  // 재발송 쿨다운 타이머
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendCooldown]);

  // 스팸함 안내 60초 지연 노출 타이머
  useEffect(() => {
    if (!lastSentAt) {
      setCanShowSpamNotice(false);
      return;
    }
    const elapsed = Math.floor((Date.now() - lastSentAt) / 1000);
    if (elapsed >= COOLDOWN_SECONDS) {
      setCanShowSpamNotice(true);
      return;
    }
    setCanShowSpamNotice(false);
    const remainingMs = (COOLDOWN_SECONDS - elapsed) * 1000;
    const timer = setTimeout(() => {
      setCanShowSpamNotice(true);
    }, remainingMs);
    return () => clearTimeout(timer);
  }, [lastSentAt]);

  // 이메일 입력 단계 복귀 시 쿨다운 및 발송시각 리셋 보장
  useEffect(() => {
    if (step === 'email') {
      setResendCooldown(0);
      setLastSentAt(null);
    }
  }, [step]);

  // 브라우저 뒤로가기(popstate) 대응: 'code' 단계에서 뒤로가기 시 이탈 대신 'email' 복귀 및 초기화
  useEffect(() => {
    const handlePopState = () => {
      if (step === 'code') {
        setStep('email');
        setError('');
        setCode('');
        setResendCooldown(0);
        setLastSentAt(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [step]);

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const handleGoogleLogin = async (force = false) => {
    if (!force && isInAppBrowser()) {
      setToastConfig({
        message: t('auth.inapp_browser_google_restriction'),
        duration: 4000,
      });
      return;
    }

    if (isGoogleLoading) return;
    setIsGoogleLoading(true);
    try {
      const hl = normalizeGoogleHl(i18n?.language);
      const options = {
        redirectTo: window.location.origin,
      };
      if (hl) {
        options.queryParams = { hl };
      }

      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options,
      });
      if (oauthError) {
        if (isDeletionCooldownError(oauthError)) {
          setToastConfig({
            message: t('auth.error_deletion_cooldown'),
            duration: 4000,
          });
        } else {
          setToastConfig({
            message: t('auth.google_login_failed'),
            duration: 3000,
          });
        }
        setIsGoogleLoading(false);
      }
    } catch (_) {
      setToastConfig({
        message: t('auth.google_login_failed'),
        duration: 3000,
      });
      setIsGoogleLoading(false);
    }
  };

  const onGoogleBtnClick = () => {
    if (!isInAppBrowser()) {
      handleGoogleLogin(false);
      return;
    }
    if (isAndroid()) {
      openInBrowser(() => setInappNotice('android_failed'));
      return;
    }
    if (isIOS()) {
      setInappNotice('ios');
      return;
    }
    setInappNotice('generic');
  };

  // 안드로이드 인앱 브라우저에서 외부 브라우저(크롬) 전환 시 구글 OAuth 1회 자동 실행
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // (1) 외부 브라우저에서 열렸을 때만
    if (isInAppBrowser()) return;

    // (2) URL 쿼리의 auto_oauth === '1'
    const params = new URLSearchParams(window.location.search);
    if (params.get('auto_oauth') !== '1') return;

    // (3) OAuth 에러 파라미터가 감지된 경우 자동 실행 건너뜀 (에러로 돌아온 사용자 재호출 방지)
    if (detectOAuthError()) return;

    // (4) sessionStorage의 소비 플래그 확인
    const consumed = sessionStorage.getItem('AUTO_OAUTH_CONSUMED');
    if (consumed === '1') return;

    // ① sessionStorage에 소비 플래그 기록 (1회성 실행 보장)
    try {
      sessionStorage.setItem('AUTO_OAUTH_CONSUMED', '1');
    } catch (_) {}

    // ② URL에서 auto_oauth 파라미터만 즉시 동기 제거 (뒤로가기 무한 루프 차단)
    try {
      const current = new URL(window.location.href);
      current.searchParams.delete('auto_oauth');
      const cleanSearch = current.search ? current.search : '';
      const cleanUrl = current.pathname + cleanSearch + current.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    } catch (e) {
      console.warn('[Auth] Failed to clean auto_oauth param:', e);
    }

    // ③ 그 다음에 handleGoogleLogin(false) 호출
    handleGoogleLogin(false);
  }, []);

  // 6초 안전장치: 구글 화면으로 이동하지 못하고 화면에 남은 경우 평상시 로그인 화면으로 복귀
  useEffect(() => {
    if (!isAutoOAuthRedirecting) return;
    const timer = setTimeout(() => {
      setIsAutoOAuthRedirecting(false);
    }, 6000);
    return () => clearTimeout(timer);
  }, [isAutoOAuthRedirecting]);

  const handleSendCode = async (e) => {
    if (e) e.preventDefault();
    if (step !== 'email') return;
    setError('');
    if (!isValidEmail(email)) {
      setError(t('auth.error_invalid_email'));
      return;
    }
    
    setLoading(true);

    const userLang = (typeof window !== 'undefined' ? localStorage.getItem('myLanguage') : '')?.trim();
    const { error: sendError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: userLang
        ? { shouldCreateUser: true, data: { my_language: userLang } }
        : { shouldCreateUser: true },
    });
    setLoading(false);
    if (sendError) {
      if (isDeletionCooldownError(sendError)) {
        setError(t('auth.error_deletion_cooldown'));
        return;
      }
      if (sendError?.status === 429) {
        setToastConfig({ message: t('auth.error_cooldown_wait'), duration: 3000 });
        setResendCooldown(COOLDOWN_SECONDS);
        setLastSentAt(Date.now());
        window.history.pushState({ step: 'code' }, '');
        setStep('code');
      } else {
        setError(t('auth.error_generic'));
      }
      return;
    }
    try {
      localStorage.setItem(LAST_AUTH_EMAIL, email.trim());
    } catch (_) {}
    setLastSentAt(Date.now());
    setResendCooldown(0);
    window.history.pushState({ step: 'code' }, '');
    setStep('code');
  };

  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    if (step !== 'code') return;
    setError('');
    if (code.trim().length !== 6) {
      setError(t('auth.error_code_incomplete'));
      return;
    }
    setLoading(true);
    const { error: verifyError, data: verifyData } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: code.trim(),
      type: 'email',
    });
    setLoading(false);
    if (verifyError) {
      if (isDeletionCooldownError(verifyError)) {
        setError(t('auth.error_deletion_cooldown'));
      } else {
        setError(t('auth.error_invalid_code'));
      }
      return;
    }
    sessionStorage.removeItem('auth_step');
    sessionStorage.removeItem('auth_email');

    let userId = verifyData?.user?.id || verifyData?.session?.user?.id;
    if (!userId) {
      const { data: { session } } = await supabase.auth.getSession();
      userId = session?.user?.id;
    }

    if (userId) {
      const { data: existingProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) {
        // [방패 3] error 발생 시 오류 상태이므로 /setup-profile로 보내지 않고 /chat-list로 보내며 정밀 판정은 App.jsx 가드에 위임
        navigate('/chat-list');
      } else if (existingProfile) {
        // 행 있음 -> /chat-list (기존과 같음)
        navigate('/chat-list');
      } else {
        // error 없이 0건 -> /setup-profile (기존과 같음)
        navigate('/setup-profile');
      }
    } else {
      // userId 확인 실패 시에도 /setup-profile로 단정하지 않고 /chat-list로 위임
      navigate('/chat-list');
    }
  };

  const handleResendCode = async (e) => {
    if (e) e.preventDefault();
    if (step !== 'code') return;
    if (loading || resendCooldown > 0) return;
    setError('');

    const elapsedSeconds = lastSentAt ? Math.floor((Date.now() - lastSentAt) / 1000) : COOLDOWN_SECONDS;
    const remainingSeconds = COOLDOWN_SECONDS - elapsedSeconds;

    if (remainingSeconds > 0) {
      setResendCooldown(remainingSeconds);
      setToastConfig({ message: t('auth.error_cooldown_wait'), duration: 3000 });
      return;
    }

    setLoading(true);
    const userLang = (typeof window !== 'undefined' ? localStorage.getItem('myLanguage') : '')?.trim();
    const { error: sendError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: userLang
        ? { shouldCreateUser: true, data: { my_language: userLang } }
        : { shouldCreateUser: true },
    });
    setLoading(false);
    if (sendError) {
      if (isDeletionCooldownError(sendError)) {
        setError(t('auth.error_deletion_cooldown'));
      } else if (sendError?.status === 429) {
        setResendCooldown(COOLDOWN_SECONDS);
        setLastSentAt(Date.now());
        setToastConfig({ message: t('auth.error_cooldown_wait'), duration: 3000 });
      } else {
        setError(t('auth.error_generic'));
      }
    } else {
      const now = Date.now();
      setLastSentAt(now);
      setResendCooldown(COOLDOWN_SECONDS);
      setToastConfig({ message: t('auth.code_sent', { email: email.trim() }), duration: 3000 });
    }
  };

  const handleChangeEmail = () => {
    setStep('email');
    setCode('');
    setError('');
    setResendCooldown(0);
    setLastSentAt(null);
    sessionStorage.removeItem('auth_step');
    sessionStorage.removeItem('auth_email');
  };

  if (isAutoOAuthRedirecting) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-md md:p-xl font-body-md relative overflow-hidden min-h-screen">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary-container/10 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-secondary-container/20 rounded-full blur-3xl -z-10"></div>

        <main className="w-full max-w-[400px] flex flex-col items-center gap-lg relative z-10 glass-panel custom-shadow rounded-3xl p-xl border border-surface-container-high/50 text-center">
          <div className="w-[120px] h-[90px] flex items-center justify-center shrink-0 overflow-hidden mb-xs">
            <Logo className="w-[90px] h-[90px] shrink-0 object-contain" />
          </div>

          <div className="flex flex-col items-center gap-md my-sm">
            <span className="material-symbols-outlined text-[36px] animate-spin text-primary">progress_activity</span>
            <p className="font-body-lg text-body-lg text-on-surface-variant font-medium">
              {t('auth.redirecting')}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-md md:p-xl font-body-md relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary-container/10 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-secondary-container/20 rounded-full blur-3xl -z-10"></div>

      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 z-20 text-primary hover:bg-surface-container transition-colors active:scale-95 p-2 rounded-full flex items-center justify-center"
      >
        <span className="material-symbols-outlined text-[24px]">arrow_back</span>
      </button>

      <main className="w-full max-w-[400px] flex flex-col items-center gap-xl relative z-10 glass-panel custom-shadow rounded-3xl p-lg border border-surface-container-high/50">

        <div className="w-full flex justify-end pr-4 -mb-xl relative z-50">
          <PwaInstallButton
            guideActive={isGuideActive}
            onInAppNoticeChange={setIsInstallNoticeOpen}
            onRequestCloseGuide={handleCloseGuide}
          />
        </div>
        {isGuideActive && (
          <PwaInstallGuide
            onClose={handleCloseGuide}
            offsetClass="top-[52px]"
            hideBubble={isInstallNoticeOpen}
          />
        )}

        <div className="w-[168px] h-[120px] flex items-center justify-center shrink-0 overflow-hidden mb-sm">
          <Logo className="w-[120px] h-[120px] shrink-0 object-contain" />
        </div>

        <div className="text-center space-y-sm w-full">
          <h1 className="font-display-lg text-display-lg text-primary">
            {t('auth.title')}
          </h1>
          <p className="font-body-lg text-body-lg text-on-surface-variant">
            {step === 'email' ? t('auth.subtitle') : t('auth.code_sent', { email })}
          </p>
        </div>

        <div className="w-full flex flex-col gap-md">
          {step === 'email' ? (
            <>
              <InAppBrowserNotice
                open={Boolean(inappNotice)}
                variant={inappNotice}
                onClose={() => setInappNotice(null)}
                onForceGoogleLogin={() => handleGoogleLogin(true)}
              />
              <div className="flex flex-col gap-[6px]">
                <div className="flex justify-between items-center ml-xs pl-[13px] pr-[13px]">
                  <span className="font-label-md text-[13px] text-[#E53935]/70">
                    {t('join_via_link.required')}
                  </span>
                  <button
                    type="button"
                    onClick={onGoogleBtnClick}
                    disabled={loading || isGoogleLoading || isOpening}
                    className="font-label-md text-[13px] text-primary underline hover:text-primary/80 transition-colors bg-transparent border-0 cursor-pointer p-0 disabled:opacity-50"
                  >
                    {t('join_via_link.google_login')}
                    {isInAppBrowser() && isAndroid() && (
                      <span className="material-symbols-outlined text-[14px] leading-none align-middle ml-[2px]">open_in_new</span>
                    )}
                  </button>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSendCode();
                    }
                  }}
                  placeholder={t('auth.email_placeholder')}
                  className="w-full bg-surface-container-lowest border border-outline-variant rounded-xl p-md font-body-lg text-body-lg text-on-surface placeholder:text-outline-variant custom-shadow focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <button
                type="button"
                onClick={handleSendCode}
                disabled={loading}
                className="w-full bg-primary text-on-primary font-headline-sm text-headline-sm py-md rounded-full shadow-md hover:bg-primary/90 transition-all duration-200 disabled:opacity-50"
              >
                {loading ? t('auth.sending') : t('auth.send_code')}
              </button>
            </>
          ) : (
            <>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(val);
                  setError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleVerify();
                  }
                }}
                placeholder={t('auth.code_placeholder')}
                className="w-full bg-surface-container-lowest border border-outline-variant rounded-xl p-md font-body-lg text-body-lg text-on-surface text-center tracking-widest placeholder:text-outline-variant custom-shadow focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <button
                type="button"
                onClick={handleVerify}
                disabled={loading}
                className="w-full bg-primary text-on-primary font-headline-sm text-headline-sm py-md rounded-full shadow-md hover:bg-primary/90 transition-all duration-200 disabled:opacity-50"
              >
                {loading ? t('auth.verifying') : t('auth.verify')}
              </button>
              <div className="flex items-center justify-between px-1 w-full text-[13px]">
                <button
                  type="button"
                  onClick={handleChangeEmail}
                  className="text-on-surface-variant font-label-md text-label-md py-sm hover:text-primary transition-colors duration-200"
                >
                  {t('auth.change_email')}
                </button>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading || resendCooldown > 0}
                  className={`font-label-md text-label-md py-sm transition-colors duration-200 disabled:opacity-50 ${
                    resendCooldown > 0
                      ? 'text-on-surface-variant/50 cursor-not-allowed'
                      : 'text-primary hover:underline'
                  }`}
                >
                  {resendCooldown > 0
                    ? t('auth.resend_wait', { seconds: resendCooldown, count: resendCooldown })
                    : t('auth.resend')}
                </button>
              </div>

              {canShowSpamNotice && (
                <p className="text-center font-body-sm text-[13px] text-on-surface-variant/80 px-1 break-keep">
                  {t('auth.code_sent_check_spam')}
                </p>
              )}
            </>
          )}

          {error && (
            <p className="font-body-md text-body-md text-error text-center w-full px-2 break-keep break-words">{error}</p>
          )}
        </div>
      </main>
      
      <Toast message={toastConfig.message} position="center" onClose={() => setToastConfig({ message: '', duration: 2500 })} />
    </div>
  );
}
